import React from "react";
import { useSelector } from "react-redux";

import LogInOutButton from "./header/LogInOutButton";
import { IReduxAppState } from "../reducers";
import { Trans, useTranslation } from "react-i18next";
import { Skeleton } from "@material-ui/lab";
import HelpButton from "./header/HelpButton";
import LanguagePicker from "./header/LanguagePicker";
import InfoComponent from "./info-component";
import { FaqLinks, getUrlBasedOnCurrentLanguage } from "../const/faqs-links";
import { useOrders } from "./util/OrderContext";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import { IProcessedOrders } from "./orders/constants";
import { IOrders } from "../interfaces/IOrder";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainHeader: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      "&>div": {
        display: "flex",
        height: "100%",
        justifyContent: "flex-end",
        alignItems: "center",
        flexGrow: "10",
        justifyItems: "end",

        "&>div": {
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          "& .MuiIconButton-label": {
            margin: "0 1rem",
          },

          "& button": {
            color: "#212121",
            backgroundColor: "#455681",
            height: "3rem",
            padding: "0 1rem",
          },
        },
      },

      "& .MuiIconButton-label": {
        height: "1.5rem!important",
      },
      "& .title": {
        display: "flex",
        alignItems: "center",
        margin: "0",
        fontFamily: "Open Sans",
        fontStyle: "normal",
        fontWeight: "600",
        fontSize: "1.25rem",
        lineHeight: "1.6875rem",
        color: "#212121",
        flexGrow: "10",
      },

      "& .logout": {
        color: "#212121",
        width: "3rem",
        height: "3rem",
      },

      "& .login": {
        color: "#212121",
      },
      "& .ordersCount": {
        border: "1px solid #757575",
        boxSizing: "border-box",
        borderRadius: "0.375rem",
        fontFamily: "Open Sans",
        fontStyle: "normal",
        fontWeight: "600",
        fontSize: "1.125rem",
        lineHeight: "1.5625rem",
        padding: "0.125rem 0.625rem",
        marginLeft: "0.625rem",
        display: "flex",
        alignItems: "center",
        textAlign: "center",
        color: "#757575",
      },
      "& button": {
        "&:hover": {
          backgroundColor: "#455681",
          "& .logoutSVG path": {
            stroke: "#ffffff",
            transition: "stroke 0.3s ease",
          },
        },
        "&:active": {
          backgroundColor: "unset!important",
        },
      },
      "& a": {
        height: "1.5rem!important",
        "&>svg": {
          height: "1.5rem!important",
        },
      },
    },

    "@media (min-width: 960px)": {
      mainHeader: {
        width: "calc(100% - 300px)",
        padding: "0 2rem",
        paddingRight: "70px",
        display: "flex",
        alignItems: "center",
        right: "0",
        position: "fixed",
      },
    },
  })
);

export interface IHeader {
  title: string;
  isLoading: boolean;
}

function Header(props: IHeader) {
  const { title, isLoading } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const titleAlt = useSelector(
    (state: IReduxAppState) => state.sidebar.position.text
  );

  const titleKey = useSelector(
    (state: IReduxAppState) => state.sidebar.position.key
  );

  const renderIcon = () => {
    if (titleKey === "OPEN_ORDERS") {
      return <OpenOrders titleKey={titleKey} />;
    } else if (titleKey === "NEW_ORDERS") {
      return <NewOrders titleKey={titleKey} />;
    } else if (!FaqLinks[titleKey] && !FaqLinks[titleKey]?.tooltip) {
      return null;
    } else {
      return <InfoComponent tooltip={<Trans i18nKey={FaqLinks[titleKey].tooltip}
        components={{ bold: <a key={titleKey} style={{ color: "#ffffff" }} href={getUrlBasedOnCurrentLanguage(FaqLinks[titleKey].key)} target="_blank" rel="noopener noreferrer"> </a> }} />} />
    }
  };

  return (
    <div className={classes.mainHeader}>
      <>
        {isLoading ? (
          <Skeleton variant="text" height={35} width={"35%"} animation="wave" />
        ) : (
          <h3 data-component-name="PAGE_TITLE" className="title">
            {t(title) || t(titleAlt)}
            {renderIcon()}
          </h3>
        )}
        <div>
          <HelpButton />
          <LanguagePicker />
          <LogInOutButton />
        </div>
      </>
    </div>
  );
}

interface IOrdersProps {
  titleKey: string;
}

function NewOrders({ titleKey }: IOrdersProps) {
  const { newOrders } = useOrders()!;
  return <OrderHeaders orders={newOrders} titleKey={titleKey} />;
}

function OpenOrders({ titleKey }: IOrdersProps) {
  const { openedOrders } = useOrders()!;
  return <OrderHeaders orders={openedOrders} titleKey={titleKey} />;
}

interface IOrdersHeadersProps extends IOrdersProps {
  orders: IProcessedOrders[] | IOrders[];
}
function OrderHeaders({ orders, titleKey }: IOrdersHeadersProps) {
  return (
    <>
      <div className="ordersCount"> {orders.length} </div>
      <InfoComponent tooltip={<Trans i18nKey={FaqLinks[titleKey].tooltip}
        components={{ bold: <a key={titleKey} style={{ color: "#ffffff" }} href={getUrlBasedOnCurrentLanguage(FaqLinks[titleKey].key)} target="_blank" rel="noopener noreferrer"> </a> }} />} />
    </>
  );
}

export default Header;
